import { useEffect, useRef } from "react"
import { ILogbookSessionType, type IAppState } from "../Types"
import { AppData } from "./AppData"
import { logger } from "./Logger"
import { Session } from "./Session"

export function useCompanionSession(app: IAppState): void {
  const { socket, apiClient, session, sessionType } = app;
  const logout = useRef(app.logout);
  logout.current = app.logout;

  useEffect(() => {
    if (sessionType !== ILogbookSessionType.local || !session?.authorized) return;
    let disposed = false;
    let ended = false;
    let checking = false;
    const end = () => {
      if (disposed || ended) return;
      ended = true;
      socket.endSession();
      void logout.current(false).catch((error) => logger.error("Companion logout failed", error));
    };
    const check = async (connected: boolean) => {
      if (connected || checking || ended || !socket.session) return;
      checking = true;
      const token = socket.session;
      try {
        const response = await AppData.loadSession(apiClient);
        if (!disposed && token === socket.session && !response.session.authorized) end();
      } catch (error) {
        if (!disposed && token === socket.session) {
          if (Session.classifySessionLoadFailure(error) === "logout") end();
          else logger.debug("Companion session check failed", error);
        }
      } finally {
        checking = false;
      }
    };
    const control = socket.subscribe("CompanionDisconnected", end);
    // A sleeping companion can miss the control message; authenticate again after
    // each failed socket attempt, without treating a network outage as revocation.
    const connection = socket.subscribeSocket((connected) => { void check(connected); });
    if (!socket.session) end();
    else if (socket.disconnected) void check(false);
    return () => {
      disposed = true;
      socket.unsubscribe("CompanionDisconnected", control);
      socket.unsubscribeSocket(connection);
    };
  }, [socket, apiClient, session?.authorized, session?.id, sessionType]);
}
